import React from 'react'
import { Link, LinkProps } from 'react-router-dom'
import { withStyles, alpha } from '@material-ui/core/styles'
import MuiIconButton, { IconButtonProps as MuiIconButtonProps } from '@material-ui/core/IconButton'

const StyledIconButton = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.grey[700]
    },
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.grey[600], 0.2)
    }
  },
  colorPrimary: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  sizeSmall: {
    padding: theme.spacing(0.75)
  }
}))(MuiIconButton)

const LightIconButton = withStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.black,
    fontSize: '3rem',
    backgroundColor: '#F6F6F7',
    '&:hover': {
      backgroundColor: '#F6F6F7'
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100]
    }
  }
}))(MuiIconButton)

const SuccessIconButton = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark
    },
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.grey[600], 0.2)
    }
  },
  sizeSmall: {
    padding: theme.spacing(0.75, 2)
  }
}))(MuiIconButton)

const DangerIconButton = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    },
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.grey[600], 0.2)
    }
  },
  sizeSmall: {
    padding: theme.spacing(0.75, 2)
  }
}))(MuiIconButton)

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  color?: MuiIconButtonProps['color'] | 'success' | 'transparent' | 'light' | 'danger'
}

export const IconButton: React.FC<IconButtonProps> = React.forwardRef(({ color, ...others }, ref) => {
  if (color === 'success') {
    return <SuccessIconButton ref={ref} {...others} />
  }
  if (color === 'transparent') {
    return <MuiIconButton ref={ref} {...others} />
  }
  if (color === 'light') {
    return <LightIconButton ref={ref} {...others} />
  }
  if (color === 'danger') {
    return <DangerIconButton ref={ref} {...others} />
  }

  return <StyledIconButton color={color} ref={ref} {...others} />
})

IconButton.displayName = 'IconButton'

export type LinkIconButtonProps = IconButtonProps & LinkProps

export const LinkIconButton: React.FC<LinkIconButtonProps> = React.forwardRef(({ color, ...others }, ref) => {
  const common = {
    ref,
    component: Link
  }

  if (color === 'success') {
    return <SuccessIconButton {...common} {...others} />
  }
  if (color === 'danger') {
    return <DangerIconButton {...common} {...others} />
  }
  if (color === 'transparent') {
    return <MuiIconButton {...common} {...others} />
  }

  return <StyledIconButton color={color} {...common} {...others} />
})

LinkIconButton.displayName = 'LinkIconButton'

export default IconButton
